import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowIcon, CancelIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import MenuPopoverCDS from 'app/shared/componentsRestyled/coreDesignSystem/MenuPopoverCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiChatTestIds } from '../../AiChat.testIds';
import { OmegaIcon } from '../../icons';
import AiChatStatusIndicator from '../AiChatStatusIndicator';

import { DEFAULT_CONFIGURATION_TITLE } from './AiChatHeader.constants';
import {
  configurationModelSkeletonStyles,
  configurationSkeletonStyles,
  configurationStackStyles,
  configurationSwitcherButtonStyles,
  configurationTitleRowStyles,
  headerActionsStyles,
  headerCloseButtonStyles,
  headerStatusBarStyles,
  headerStatusSummaryStyles,
  headerStyles,
} from './AiChatHeader.styles';

const AiChatHeader = ({
  onClose,
  isConnected = true,
  activeConfiguration = null,
  eligibleConfigurations = [],
  isLoadingConfigurations = false,
  onConfigurationSelect,
}) => {
  const switcherButtonRef = useRef(null);
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);

  const hasSwitcher = eligibleConfigurations.length > 1;
  const configurationName = activeConfiguration?.name ?? DEFAULT_CONFIGURATION_TITLE;
  const modelDisplayName = activeConfiguration?.modelDisplayName ?? null;

  const onSwitcherButtonClick = useCallback(() => {
    setIsSwitcherOpen(true);
  }, []);

  const onSwitcherClose = useCallback(() => {
    setIsSwitcherOpen(false);
  }, []);

  const menuItems = useMemo(
    () =>
      eligibleConfigurations.map(configuration => ({
        text: configuration.name,
        isDisabled: configuration.configurationId === activeConfiguration?.configurationId,
        onClick: () => onConfigurationSelect(configuration),
      })),
    [eligibleConfigurations, activeConfiguration?.configurationId, onConfigurationSelect],
  );

  const renderConfigurationBody = () => {
    if (isLoadingConfigurations) {
      return (
        <Stack
          data-testid={AiChatTestIds.AI_CHAT_HEADER_CONFIG_SKELETON}
          sx={configurationStackStyles}
        >
          <Skeleton sx={configurationSkeletonStyles} variant="text" />
          <Skeleton sx={configurationModelSkeletonStyles} variant="text" />
        </Stack>
      );
    }

    return (
      <Stack sx={configurationStackStyles}>
        <Stack sx={configurationTitleRowStyles}>
          <Typography data-testid={AiChatTestIds.AI_CHAT_HEADER_CONFIG_NAME} variant="medium16">
            {configurationName}
          </Typography>
          {hasSwitcher && (
            <IconButton
              data-testid={AiChatTestIds.AI_CHAT_HEADER_CONFIG_SWITCHER_BUTTON}
              ref={switcherButtonRef}
              sx={configurationSwitcherButtonStyles}
              onClick={onSwitcherButtonClick}
            >
              <ArrowIcon direction="down" size={16} />
            </IconButton>
          )}
        </Stack>
        {!_.isNil(modelDisplayName) && (
          <Typography data-testid={AiChatTestIds.AI_CHAT_HEADER_CONFIG_MODEL} variant="regular12">
            {modelDisplayName}
          </Typography>
        )}
        <AiChatStatusIndicator isConnected={isConnected} />
      </Stack>
    );
  };

  return (
    <Stack data-testid={AiChatTestIds.AI_CHAT_HEADER} sx={headerStyles}>
      <Stack data-testid={AiChatTestIds.AI_CHAT_HEADER_STATUS_BAR} sx={headerStatusBarStyles}>
        <Stack
          data-testid={AiChatTestIds.AI_CHAT_HEADER_STATUS_SUMMARY}
          sx={headerStatusSummaryStyles}
        >
          <OmegaIcon size={36} />
          {renderConfigurationBody()}
        </Stack>
        <Stack sx={headerActionsStyles}>
          <IconButton
            data-testid={AiChatTestIds.AI_CHAT_HEADER_CLOSE_BUTTON}
            sx={headerCloseButtonStyles}
            onClick={onClose}
          >
            <CancelIcon size={16} />
          </IconButton>
        </Stack>
      </Stack>

      {hasSwitcher && isSwitcherOpen && (
        <MenuPopoverCDS
          anchorEl={switcherButtonRef.current}
          data-testid={AiChatTestIds.AI_CHAT_HEADER_CONFIG_SWITCHER_MENU}
          items={menuItems}
          onClose={onSwitcherClose}
        />
      )}
    </Stack>
  );
};

AiChatHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfigurationSelect: PropTypes.func.isRequired,
  isConnected: PropTypes.bool,
  activeConfiguration: PropTypes.shape({
    configurationId: PropTypes.string,
    name: PropTypes.string,
    modelDisplayName: PropTypes.string,
  }),
  eligibleConfigurations: PropTypes.arrayOf(
    PropTypes.shape({
      configurationId: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  isLoadingConfigurations: PropTypes.bool,
};

export default memo(AiChatHeader);
